import { Car } from "lucide-react";
import { Skeleton } from "./skeleton";
import { cn } from "@/lib/utils";

interface AutomotiveLoaderProps {
  className?: string;
  message?: string;
  subtitle?: string;
}

const AutomotiveLoader = ({
  className,
  message = "Loading Auttom",
  subtitle = "Initializing your automotive management system..."
}: AutomotiveLoaderProps) => {
  return (
    <div className={cn(
      "min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 flex items-center justify-center p-4",
      className
    )}>
      <div className="flex flex-col items-center gap-8 w-full max-w-md">
        {/* Automotive icon with pulse animation */}
        <div className="relative">
          <div className="w-20 h-20 bg-red-600 rounded-full flex items-center justify-center animate-pulse shadow-lg">
            <Car className="w-10 h-10 text-white" />
          </div>
          <div className="absolute inset-0 w-20 h-20 bg-blue-600/20 rounded-full animate-ping" />
        </div>

        {/* Loading text */}
        <div className="text-center space-y-2">
          <h1 className="text-xl font-semibold text-gray-900">{message}</h1>
          <p className="text-sm text-gray-600">{subtitle}</p>
        </div>

        {/* Skeleton dashboard preview */}
        <div className="w-full space-y-4">
          {/* Header skeleton */}
          <div className="flex items-center gap-4 p-4 bg-white rounded-lg shadow-sm">
            <Skeleton className="w-12 h-12 rounded-full" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-3 w-20" />
            </div>
          </div>

          {/* Dashboard cards skeleton */}
          <div className="space-y-3">
            {[1, 2, 3].map((index) => (
              <div key={index} className="p-4 bg-white rounded-lg shadow-sm space-y-3">
                <div className="flex justify-between items-center">
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="h-4 w-16" />
                </div>
                <Skeleton className="h-3 w-full" />
                <Skeleton className="h-3 w-4/5" />
              </div>
            ))}
          </div>

          {/* Progress indicator */}
          <div className="flex justify-center">
            <div className="flex space-x-1">
              {[0, 1, 2].map((index) => (
                <div
                  key={index}
                  className={cn(
                    "w-2 h-2 rounded-full bg-red-600 animate-pulse",
                    "animate-bounce"
                  )}
                  style={{
                    animationDelay: `${index * 0.2}s`,
                    animationDuration: "1s"
                  }}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export { AutomotiveLoader };
export default AutomotiveLoader;